import { useEffect, useState } from 'react';
import { ChevronLeft, Pencil } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, videoStreamUrl, videoThumbUrl, type Video } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { formatDateFull } from '../lib/format.ts';
import { PlayerControls } from '../player/PlayerControls.tsx';
import { useVideoPlayer } from '../player/useVideoPlayer.ts';
import './PlayerScreen.css';

export function PlayerScreen() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [video, setVideo] = useState<Video | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    videoRef,
    containerRef,
    state,
    preload,
    togglePlay,
    toggleMute,
    toggleFullscreen,
    seekTo,
    seekBack,
    seekForward,
    revealControls,
    toggleControls,
  } = useVideoPlayer(video?.durationSec ?? 0);

  useEffect(() => {
    let cancelled = false;
    api
      .getVideo(id)
      .then((v) => {
        if (!cancelled) setVideo(v);
      })
      .catch(() => {
        if (!cancelled) setError('Видео не найдено');
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <div className="screen player-screen">
      <div
        ref={containerRef}
        className="player-stage"
        data-fullscreen={state.fullscreen ? 'true' : 'false'}
        data-rotated={state.rotated ? 'true' : 'false'}
      >
        {/* Always rendered so the ref exists at mount and the media event
            listeners attach — otherwise play/pause state never updates. */}
        <video
          ref={videoRef}
          className="player-video"
          src={video ? videoStreamUrl(video.id) : undefined}
          poster={video?.hasThumb ? videoThumbUrl(video.id) : undefined}
          playsInline
          preload={preload}
        />
        <PlayerControls
          state={state}
          onTogglePlay={togglePlay}
          onToggleMute={toggleMute}
          onToggleFullscreen={toggleFullscreen}
          onSeek={seekTo}
          onSeekBack={seekBack}
          onSeekForward={seekForward}
          onSurfaceTap={toggleControls}
          onActivity={revealControls}
        />
      </div>

      <button
        type="button"
        className={`icon-button player-back${state.controlsVisible ? '' : ' player-chrome-hidden'}`}
        onClick={() => navigate('/')}
        aria-label="Назад к списку"
      >
        <ChevronLeft size={20} />
      </button>

      <div className="player-meta">
        {error && <div className="form-error">{error}</div>}
        {video && (
          <>
            <div className="player-meta__text">
              <div className="player-meta__title">{video.title}</div>
              <div className="player-meta__date">{formatDateFull(video.tripDate)}</div>
            </div>
            {/* The only entry point to editing — viewers never see it. */}
            {user?.isAdmin && (
              <button
                type="button"
                className="icon-button player-edit"
                onClick={() => navigate(`/video/${video.id}/edit`)}
                aria-label="Изменить видео"
              >
                <Pencil size={18} strokeWidth={2} />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
